import type { FC } from 'react';
import { TextInput } from '@interstate/components/TextInput';
import { EmailInput } from '@interstate/components/EmailInput';
import { PhoneInput } from '@interstate/components/PhoneInput';
import { Button } from '@interstate/components/Button';
import { dealLockHooks } from '../../hooks';

import type { SearchViewProps } from '../types';
import { SearchFormContainer, SearchInputGroup, SearchButtonGroup, SearchDescription } from '../CreditAppSearch.styles';
import { Typography } from '@interstate/components/Typography';

const DEFAULT_DESCRIPTION =
    'Search for customer information from leads, credit applications or saved deals to merge with the current deal.';

/**
 * Search form view component for credit app search
 * Displays input fields for last name, phone, and email
 * with validation and Clear/Search buttons
 */
export const SearchView: FC<SearchViewProps> = ({
    formData,
    errors,
    touched,
    isSearchButtonEnabled,
    onFieldChange,
    onFieldBlur,
    onClear,
    onSearch,
    description = DEFAULT_DESCRIPTION
}) => {
    const isLocked = dealLockHooks.useIsDealLocked();

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && isSearchButtonEnabled) {
            event.preventDefault();
            onSearch();
        }
    };

    return (
        <SearchFormContainer onKeyDown={handleKeyDown}>
            <Typography variant="h4" data-testid="credit-app-search-subheading">
                Search Criteria
            </Typography>
            <SearchDescription>
                <Typography variant="body-sm" data-testid="credit-app-search-description">
                    {description}
                </Typography>
            </SearchDescription>
            <SearchInputGroup>
                <TextInput
                    autoFocus
                    disabled={isLocked}
                    name="lastName"
                    label="Last Name"
                    placeholder="Enter last name"
                    value={formData.lastName}
                    hasError={touched.lastName && Boolean(errors.lastName)}
                    errorMessage={touched.lastName ? errors.lastName : undefined}
                    onChange={(evt) => onFieldChange('lastName', evt.target.value)}
                    onBlur={() => onFieldBlur('lastName')}
                    data-testid="credit-app-search-last-name"
                />
                <PhoneInput
                    disabled={isLocked}
                    name="phone"
                    label="Phone"
                    placeholder="Enter phone number"
                    value={formData.phone}
                    hasError={touched.phone && Boolean(errors.phone)}
                    onChange={(evt) => {
                        onFieldChange('phone', evt.target.value);
                    }}
                    onBlur={() => onFieldBlur('phone')}
                    data-testid="credit-app-search-phone"
                    displayLabel={true}
                />
            </SearchInputGroup>
            <EmailInput
                disabled={isLocked}
                name="email"
                label="Email"
                placeholder="Enter email address"
                value={formData.email}
                hasError={touched.email && Boolean(errors.email)}
                onChange={(evt) => onFieldChange('email', evt.target.value)}
                onBlur={() => onFieldBlur('email')}
                data-testid="credit-app-search-email"
            />
            <SearchButtonGroup>
                <Button
                    buttonStyle="tertiary"
                    size="medium"
                    onClick={onClear}
                    data-testid="credit-app-search-clear-button"
                    disabled={isLocked}
                >
                    Clear
                </Button>
                <Button
                    buttonStyle="primary"
                    size="medium"
                    onClick={onSearch}
                    disabled={!isSearchButtonEnabled || isLocked}
                    data-testid="credit-app-search-search-button"
                >
                    Search
                </Button>
            </SearchButtonGroup>
        </SearchFormContainer>
    );
};
